import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { Subject, Subscription } from 'rxjs';
import { NotifyService } from 'src/app/service/notify.service';
import { PosthogService } from 'src/app/service/posthog.service';
import { ConfirmDialogComponent } from '../../a2p/common/confirm-dialog/confirm-dialog.component';
import { defaultTableDefOptions } from '../../helpers';
import { CustomerPortalService } from '../../service/customer-portal.service';
import { PanoptycAdminService } from '../../service/panoptyc-admin.service';
import { PresenceResponse, SignalrPresenceService } from '../../service/signalr-presence.service';
import { SubscriptionService } from '../../service/subscription.service';
import {
  AvailableCamera,
  AvailableCamerasCache,
  Branch,
  BranchImpl,
  BundleSummary,
  CustomerType,
  EditModeContextWithOriginalState,
  FeatureFlag,
  LocationImpl,
  LocationPlanMap,
} from '../../types';
import { getLocationStatus } from '../util/stripe.utils';
import { CellSubscriptionsActionsHeaderComponent } from './cell-subscriptions-actions-header/cell-subscriptions-actions-header.component';
import { CellSubscriptionsActionsComponent } from './cell-subscriptions-actions/cell-subscriptions-actions.component';
import { CellSubscriptionsCamerasComponent } from './cell-subscriptions-cameras/cell-subscriptions-cameras.component';
import { CellSubscriptionsPlanComponent } from './cell-subscriptions-plan/cell-subscriptions-plan.component';
import { CellSubscriptionsStatusComponent } from './cell-subscriptions-status/cell-subscriptions-status.component';
import { CellSubscriptionsTrialComponent } from './cell-subscriptions-trial/cell-subscriptions-trial.component';

type PageTab = 'location' | 'bundlesummary' | 'bundlehistory' | 'bundlepurchase' | 'order';

@Component({
  selector: 'app-stripe-subscription-manage-card',
  templateUrl: './stripe-subscription-manage-page.component.html',
  styleUrls: ['./stripe-subscription-manage-page.component.scss'],
  standalone: false,
})
export class StripeSubscriptionManagePageComponent implements OnInit {
  private adminService = inject(PanoptycAdminService);
  private dataService = inject(CustomerPortalService);
  private subscriptionService = inject(SubscriptionService);
  private presenceService = inject(SignalrPresenceService);
  public dialog = inject(MatDialog);
  public posthogService = inject(PosthogService);
  private notifyService = inject(NotifyService);

  public branchId = input<string>('');
  public branchData = input<Branch>();

  public branch = signal<BranchImpl | undefined>(undefined);
  public editMode = signal<boolean>(false);
  public isSaving = signal<boolean>(false);
  public compatibleTypes = signal<CustomerType[]>([]);
  public selectedCustomerType = signal<string>(null);

  public branchIdNum = computed(() => +this.branchId());
  public locations = computed(() => this.branch().getLocations());

  readonly view = linkedQueryParam<PageTab>('view', {
    parse: (value) => (value ?? 'location').toLowerCase() as PageTab,
  });

  public readonly tableDef: GridOptions<LocationImpl> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly columns: ColDef<LocationImpl>[] = [
    {
      field: 'name',
      sortable: false,
      tooltipField: 'name',
      headerName: 'Location',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
    },
    {
      field: 'stripePriceId',
      sortable: false,
      headerName: 'Plan',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        const priceId = params.data.stripePriceId;
        const plan = this.stripeProductPrices.find((p) => p.id === priceId);
        return plan ? plan.nickname : 'No Theft Plan';
      },
      cellRenderer: CellSubscriptionsPlanComponent,
      cellRendererParams: {
        isSaving: () => this.isSaving(),
        isEditing: () => this.editMode(),
        editContext: () => this.editContext,
        stripeProductPrices: () => this.stripeProductPrices,
      },
    },
    {
      field: 'trialEnd',
      sortable: false,
      headerName: 'Trial',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        const trialInfo = this.getTrial(params.data.trialEnd);
        return trialInfo.text;
      },
      cellRenderer: CellSubscriptionsTrialComponent,
      cellRendererParams: {
        isSaving: () => this.isSaving(),
        isEditing: () => this.editMode(),
        editContext: () => this.editContext,
      },
    },
    {
      field: 'status',
      sortable: false,
      headerName: 'Status',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        return getLocationStatus(params.data);
      },
      cellRenderer: CellSubscriptionsStatusComponent,
      cellRendererParams: {
        isSaving: () => this.isSaving(),
        isEditing: () => this.editMode(),
        editContext: () => this.editContext,
      },
    },
    {
      field: 'assignedCameras',
      sortable: false,
      headerName: 'Cameras',
      cellRenderer: CellSubscriptionsCamerasComponent,
      cellRendererParams: {
        isSaving: () => this.isSaving(),
        isEditing: () => this.editMode(),
        editContext: () => this.editContext,
        availableCameras: (loc: LocationImpl) => this.getAvailableCameras(loc),
      },
    },
    {
      cellRenderer: CellSubscriptionsActionsComponent,
      cellRendererParams: {
        isEditing: () => this.editMode(),
        branchId: () => this.branch()?.id,
      },
      headerComponent: CellSubscriptionsActionsHeaderComponent,
      headerComponentParams: {
        isEditing: () => this.editMode(),
        isSaving: () => this.isSaving(),
        editEnable: () => this.enableEditMode(),
        editCancel: () => this.cancelEdit(),
        editSave: () => this.onSave(),
      },
    },
  ];

  stripeProductPrices: any[] = [];

  availableCameras: AvailableCamera[] = [];
  bundleSummaries: BundleSummary[] = [];
  triggerBundleRefresh = new Subject<boolean>();

  public presence: PresenceResponse = [null, []];
  private presenceSubscription?: Subscription = null;
  private memoizedTrial = new Map<Date, { endDate: Date; tooltip: string; text: string; editable: boolean }>();

  public editContext: EditModeContextWithOriginalState[] = [];

  private locationPlanMap: LocationPlanMap = {};
  private availableCamerasCache: AvailableCamerasCache = {};

  isLoading = signal<boolean>(false);

  ngOnInit(): void {
    if (!this.branchId()) {
      return;
    }
    this.adminService.getCompatibleCustomerTypes(this.branchId()).subscribe((types) => {
      this.compatibleTypes.set(types);
    });

    this.isLoading.set(true);

    this.adminService.getBranchById(this.branchId()).subscribe((d) => {
      const branch = this.adminService.createBranchFromAPIResponse(d, this.branchIdNum());
      this.branch.set(branch);

      this.subscriptionService.getTheftPlans(this.branch().customerType).subscribe((prices) => {
        this.stripeProductPrices = prices;
        this.isLoading.set(false);
      });

      this.presenceService.connectPresencesHub().subscribe();
    });
  }

  confirmDialog(locationCount: number): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Restart Location Trials`,
        message: `${locationCount} locations you're updating had trialed previously.
          Restarting the trial will reset the trial period for these locations.
          Are you sure you want to restart the trial?`,
      },
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.actuallySave();
      }
    });
  }

  onSave() {
    let locationsToConfirm = 0;
    this.editContext.forEach((context, index) => {
      const warnAboutTrial = context.selectedTrial > 0 && this.locations()[index].trialEnd != null;
      if (context.selectedPriceId && warnAboutTrial) {
        locationsToConfirm++;
      }
    });

    if (locationsToConfirm > 0) {
      return this.confirmDialog(locationsToConfirm);
    }

    return this.actuallySave();
  }

  enableEditMode() {
    const roomName = `SUBSCRIPTION_EDIT_${this.branch().id}`;
    this.presenceSubscription = this.presenceService.joinRoom(roomName).subscribe((data: PresenceResponse) => {
      this.presence = data;
    });
    this.isSaving.set(false);
    this.editMode.set(true);
    this.editContext = this.locations().map((location) => {
      const currentContext = {
        id: location.id,
        selectedPriceId: location.stripePriceId,
        status: location.status !== 'NewLocation' ? location.status : 'default',
        selectedCameras: location.assignedCameras,
      };
      return {
        originalState: {
          ...currentContext,
        },
        ...currentContext,
      };
    });

    this.locations().forEach((location) => {
      this.locationPlanMap[location.id] = {
        current: {
          price_id: location.stripePriceId,
        },
        previous: {
          price_id: location.stripePriceId,
        },
      };
    });
  }

  actuallySave() {
    if (this.isSaving()) return;

    this.posthogService.capture('update_theft_plan', {
      admin: true,
      branchId: this.branch().id,
      planMap: this.locationPlanMap,
    });

    this.isSaving.set(true);

    const daysToMsMultiplier = 24 * 60 * 60 * 1000;

    this.editContext.forEach((context, index) => {
      const location = this.locations()[index];
      const newStatus = context.status
        ? getLocationStatus({
            status: context.status,
            stripePriceId: context.selectedPriceId,
            trialEnd: context.selectedTrial
              ? // create a new date object with the current date + the number of days in the trial
                new Date(new Date().getTime() + context.selectedTrial * daysToMsMultiplier)
              : undefined,
          } as LocationImpl)
        : getLocationStatus(location);

      this.locationPlanMap[location.id] = {
        current: {
          price_id: context.selectedPriceId,
          trial_days: context.selectedTrial ?? undefined,
          status: newStatus,
          cameras: context.selectedCameras,
        },
        previous: {
          price_id: context.originalState.selectedPriceId,
          trial_days: context.originalState.selectedTrial ?? undefined,
          status: context.originalState.status,
          cameras: context.originalState.selectedCameras,
        },
      };
    });

    this.subscriptionService
      .updateSubscription({
        branch_id: this.branch().id,
        location_plans: Object.keys(this.locationPlanMap).map((key) => {
          return {
            location_id: parseInt(key),
            location_plan: this.locationPlanMap[key].current,
          };
        }),
        previous_plans: Object.keys(this.locationPlanMap).map((key) => {
          return {
            location_id: parseInt(key),
            location_plan: this.locationPlanMap[key].previous,
          };
        }),
      })
      .subscribe({
        next: () => {
          this.locations().forEach((location, index) => {
            const context = this.editContext[index];
            location.stripePriceId = context.selectedPriceId;
            location.status = context.status;
            location.assignedCameras = context.selectedCameras;

            const trialEndDate = new Date();
            if (context.selectedTrial >= 0) {
              trialEndDate.setDate(trialEndDate.getDate() + context.selectedTrial);
              location.trialEnd = trialEndDate;
            }
          });

          this.triggerBundleRefresh.next(true);
          this.editContext = [];
          this.editMode.set(false);
          this.isSaving.set(false);
          this.presenceSubscription?.unsubscribe();
        },
        error: (error) => {
          const customErrorMessage = error.error?.title ?? error.error?.error;
          if (customErrorMessage) {
            this.notifyService.notify(customErrorMessage);
          } else {
            this.notifyService.notify('Error saving subscription plan');
          }

          this.isSaving.set(false);
        },
      });
  }

  cancelEdit() {
    if (this.isSaving()) return;

    this.editContext = [];
    this.editMode.set(false);
    this.presenceSubscription?.unsubscribe();
  }

  onManageBillingClicked(branch: BranchImpl, event?: Event) {
    if (event) {
      event?.stopPropagation();
    }

    this.dataService.createBillingSession(branch.id).subscribe((response) => {
      window.open(response.url, '_blank');
    });
  }

  showStripeLink(branch: BranchImpl): boolean {
    return (
      !!branch.stripeCustomerId &&
      !(this.posthogService.isReady() && this.posthogService.isFeatureEnabledByEnum(FeatureFlag.EnableNetsuiteMigration))
    );
  }

  getTrial(trialEndDate?: Date) {
    if (this.memoizedTrial.has(trialEndDate)) {
      return this.memoizedTrial.get(trialEndDate);
    }
    const returnValue = {
      endDate: trialEndDate,
      tooltip: trialEndDate ? 'Trial ends on ' + trialEndDate.toLocaleDateString() : 'No trial',
      text: 'No trial',
      editable: true,
    };

    if (trialEndDate === null || trialEndDate === undefined) {
      return returnValue;
    }

    const dateNow = new Date();
    if (dateNow < trialEndDate) {
      const timeDiff = trialEndDate.getTime() - dateNow.getTime();
      const dayDifference = Math.floor(timeDiff / (1000 * 3600 * 24));
      returnValue.text = `${dayDifference} days remaining`;
      returnValue.editable = false;
    } else {
      returnValue.text = `Ended on ${trialEndDate.toLocaleDateString()}`;
      returnValue.tooltip = 'Trial ended';
      returnValue.editable = true;
    }
    this.memoizedTrial.set(trialEndDate, returnValue);
    return returnValue;
  }

  onBundleSummariesUpdated(bundleSummaries: BundleSummary[]) {
    this.availableCamerasCache = {};
    this.availableCameras = [];
    this.bundleSummaries = bundleSummaries;
    bundleSummaries.forEach((summary) => {
      const available = summary.total_quantity - summary.assigned;
      if (available > 0) {
        for (let i = 0; i < available; i++) {
          this.availableCameras.push({
            id: `${summary.bundle_transaction_id}-${i}`,
            name: summary.bundle,
            transactionId: summary.bundle_transaction_id,
            available,
          });
        }
      }
    });
  }

  getAvailableCameras(location: LocationImpl) {
    if (this.availableCamerasCache[location.id]) {
      return this.availableCamerasCache[location.id];
    }
    const available: AvailableCamera[] = [];
    location.assignedCameras?.forEach((camera, index) => {
      const cameraDetails = this.bundleSummaries.find((summary) => {
        return summary.bundle_transaction_id === camera;
      });

      if (cameraDetails) {
        available.push({
          id: `${camera}-${index}`,
          name: cameraDetails.bundle,
          transactionId: cameraDetails.bundle_transaction_id,
          available: cameraDetails.total_quantity - cameraDetails.assigned,
        });
      }
    });
    available.push(...this.availableCameras);
    this.availableCamerasCache[location.id] = available;
    return available;
  }

  reclassify() {
    this.adminService.reclassifyBranch(this.branchIdNum(), this.selectedCustomerType()).subscribe(() => {
      location.reload();
    });
  }
}
